import { useState, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { FORMATS, convertImage, outputName } from '../utils/imageConvert';

const smallButton = {
  background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
  padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600,
};
const primaryButton = {
  background: '#0369a1', color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 700,
};

const download = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

let nextId = 1;

export default function ImageConverter() {
  const [format, setFormat] = useState('jpg');
  const [quality, setQuality] = useState(92);
  const [items, setItems] = useState([]); // {id, file, status, blob, error}
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  const patch = (id, p) => setItems(prev => prev.map(i => (i.id === id ? { ...i, ...p } : i)));

  const addFiles = (fileList) => {
    const added = Array.from(fileList || []).map(file => ({ id: nextId++, file, status: 'queued' }));
    if (added.length) setItems(prev => [...prev, ...added]);
  };

  const changeFormat = (f) => {
    setFormat(f);
    // Previous results are for the old format; send everything back to the queue.
    setItems(prev => prev.map(i => ({ ...i, status: 'queued', blob: undefined, error: undefined })));
  };

  const convertAll = useCallback(async () => {
    setRunning(true);
    trackToolAction('/imageconverter', 'Image Converter');
    const todo = items.filter(i => i.status !== 'done');
    for (const item of todo) {
      patch(item.id, { status: 'working' });
      try {
        const blob = await convertImage(item.file, format, quality / 100);
        patch(item.id, { status: 'done', blob });
      } catch (err) {
        patch(item.id, { status: 'failed', error: err?.message || String(err) });
      }
    }
    setRunning(false);
  }, [items, format, quality]);

  const downloadZip = async () => {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const used = new Set();
    items.filter(i => i.blob).forEach(i => {
      let name = outputName(i.file.name, format);
      while (used.has(name)) name = name.replace(/(\.\w+)$/, '_1$1');
      used.add(name);
      zip.file(name, i.blob);
    });
    download(await zip.generateAsync({ type: 'blob' }), 'converted-images.zip');
  };

  const done = items.filter(i => i.status === 'done');
  const pending = items.filter(i => i.status !== 'done').length;

  return (
    <div style={{
      minHeight: '100vh', background: '#f8fafc', color: '#0f172a',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      <Navbar pageTitle="Image Converter" />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 72px' }}>

        <div style={{
          background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12,
          padding: '14px 18px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: 20, lineHeight: 1 }}>🔒</div>
          <div style={{ fontSize: 13, color: '#065f46', lineHeight: 1.6 }}>
            <strong>Your photos never leave this computer.</strong> Conversion runs entirely in your
            browser and makes no network requests.
          </div>
        </div>

        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12,
          padding: '12px 18px', marginBottom: 24, fontSize: 13, color: '#92400e', lineHeight: 1.6,
        }}>
          <strong>Metadata is not carried over.</strong> Converted copies do not keep EXIF data
          (GPS, device, timestamps), and their hashes differ from the original. Keep the original
          HEIC as your evidence copy, and read its metadata with the EXIF Viewer.
        </div>

        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          style={{
            background: '#fff', border: '2px dashed #cbd5e1', borderRadius: 12,
            padding: '32px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700 }}>Drop HEIC photos here, or click to choose</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            .heic and .heif (also accepts JPG, PNG, WebP)
          </div>
          <input
            ref={inputRef} type="file" multiple hidden
            accept=".heic,.heif,image/heic,image/heif,image/*"
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        <div style={{
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
          padding: '16px 20px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Convert to</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            {Object.entries(FORMATS).map(([key, f]) => (
              <button
                key={key} onClick={() => changeFormat(key)} aria-pressed={format === key}
                style={{
                  ...smallButton, fontSize: 14, padding: '8px 22px',
                  ...(format === key ? { background: '#0369a1', color: '#fff', borderColor: '#0369a1' } : {}),
                }}
              >{f.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
            {format === 'jpg'
              ? 'JPG: small files that open anywhere — best for reports, email, and uploads.'
              : 'PNG: lossless, so no further quality loss, but files are several times larger.'}
          </div>
          {format === 'jpg' && (
            <div style={{ marginTop: 12 }}>
              <label htmlFor="jpg-quality" style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
                Quality: {quality}%
              </label>
              <input
                id="jpg-quality" type="range" min={50} max={100} value={quality}
                onChange={e => { setQuality(+e.target.value); setItems(p => p.map(i => ({ ...i, status: 'queued', blob: undefined }))); }}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '8px 20px', marginBottom: 20 }}>
            {items.map(i => (
              <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.file.name}</div>
                  <div style={{ fontSize: 11, color: i.status === 'failed' ? '#b91c1c' : '#94a3b8' }}>
                    {i.status === 'queued' && `Queued · ${fmtSize(i.file.size)}`}
                    {i.status === 'working' && 'Converting…'}
                    {i.status === 'done' && `Done · ${fmtSize(i.file.size)} → ${fmtSize(i.blob.size)}`}
                    {i.status === 'failed' && `Failed: ${i.error}`}
                  </div>
                </div>
                {i.status === 'done' && (
                  <button style={smallButton} onClick={() => download(i.blob, outputName(i.file.name, format))}>Download</button>
                )}
                <button
                  style={smallButton} disabled={i.status === 'working'} aria-label={`Remove ${i.file.name}`}
                  onClick={() => setItems(p => p.filter(x => x.id !== i.id))}
                >✕</button>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button style={{ ...primaryButton, opacity: running || !pending ? 0.5 : 1 }} disabled={running || !pending} onClick={convertAll}>
              {running ? 'Converting…' : `Convert ${pending} file${pending === 1 ? '' : 's'} to ${FORMATS[format].label}`}
            </button>
            {done.length > 1 && <button style={{ ...smallButton, fontSize: 14, padding: '10px 18px' }} onClick={downloadZip}>Download all (.zip)</button>}
            <button style={{ ...smallButton, fontSize: 14, padding: '10px 18px' }} disabled={running} onClick={() => setItems([])}>Clear</button>
          </div>
        )}
      </div>
    </div>
  );
}
